import React, { useState, useEffect } from "react";
import { generateClient, GraphQLResult } from "aws-amplify/data";
import {
  getSubscriptionErrorQuery,
  fixSubscriptionErrorsMutation,
  IFixSubscriptionErrorsResponse,
  removeSubscriptionErrorsMutation,
  IRemoveSubscriptionErrorsResponse,
} from "../lib";

interface ISubscriptionError {
  subscriptionId: string;
  contactId: string;
  planId: string;
  status: string;
  renewalDate: string;
  errorType: string;
  errorCode: string;
  errorDescription: string;
}

const SubscriptionErrorTable: React.FC = () => {
  const [subscriptionErrors, setSubscriptionErrors] = useState<
    ISubscriptionError[]
  >([]);
  const [selectedError, setSelectedError] = useState<ISubscriptionError | null>(
    null
  );
  const [errorCodeFilter, setErrorCodeFilter] = useState("");
  const [action, setAction] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchSubscriptionErrors = async () => {
      try {
        const client = generateClient({
          authMode: "identityPool",
        });
        const res = (await client.graphql({
          query: getSubscriptionErrorQuery,
          variables: {
            errorCode: errorCodeFilter,
          },
        })) as GraphQLResult<{ getSubscriptionErrors: ISubscriptionError[] }>;

        if (isMounted) {
          if (!res || !res.data) {
            throw new Error(
              "Invalid response from getSubscriptionErrors query"
            );
          } else {
            setSubscriptionErrors(res.data.getSubscriptionErrors.slice(0, 50));
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching subscription errors:", error);
          setSubscriptionErrors([]);
        }
      }
    };

    fetchSubscriptionErrors();

    return () => {
      isMounted = false;
    };
  }, [errorCodeFilter]);

  const fixSubscriptionError = async (
    subscriptionId: string,
    contactId: string,
    errorCode: string,
    errorDescription: string
  ) => {
    try {
      const client = generateClient({
        authMode: "identityPool",
      });
      const res = (await client.graphql({
        query: fixSubscriptionErrorsMutation,
        variables: {
          subscriptionId,
          contactId,
          errorCode,
          errorDescription,
        },
      })) as GraphQLResult<IFixSubscriptionErrorsResponse>;

      if (!res || !res.data) {
        throw new Error(
          "No data returned from the fixSubscriptionErrorsMutation"
        );
      }

      const { statusCode, error } = res.data.fixSubscriptionErrors;

      if (statusCode === 200) {
        alert("Subscription error fixed successfully!");
        setSubscriptionErrors((prevErrors) =>
          prevErrors.filter((error) => error.subscriptionId !== subscriptionId)
        );
        setSelectedError(null);
        setAction(null);
      } else if (error) {
        alert(`Error fixing subscription: ${error.message}`);
      }
    } catch (error) {
      console.error("Error fixing subscription:", error);
    }
  };

  const removeSubscriptionError = async (
    subscriptionId: string,
    errorCode: string
  ) => {
    try {
      const client = generateClient({
        authMode: "identityPool",
      });
      const res = (await client.graphql({
        query: removeSubscriptionErrorsMutation,
        variables: {
          subscriptionId,
          errorCode,
        },
      })) as GraphQLResult<IRemoveSubscriptionErrorsResponse>;

      if (!res || !res.data) {
        throw new Error(
          "No data returned from the removeSubscriptionErrorsMutation"
        );
      }

      const { statusCode, error } = res.data.removeSubscriptionErrors;

      if (statusCode === 200) {
        alert("Subscription error removed successfully!");
        setSubscriptionErrors((prevErrors) =>
          prevErrors.filter((error) => error.subscriptionId !== subscriptionId)
        );
        setSelectedError(null);
        setAction(null);
      } else if (error) {
        alert(`Error removing subscription: ${error.message}`);
      }
    } catch (error) {
      console.error("Error removing subscription:", error);
    }
  };

  const handleFixClick = (error: ISubscriptionError) => {
    setSelectedError(error);
    setAction("fix");
  };
  const handleRemoveClick = (error: ISubscriptionError) => {
    setSelectedError(error);
    setAction("remove");
  };

  const getFixConfirmationMessage = (errorCode: string) => {
    const messages: { [key: string]: string } = {
      ACTIVE_SUBSCRIPTION_RENEWAL_DATE_IN_THE_PAST:
        "Do you want to fix the subscription error by setting the renewal date 1 month in the future?",
      PROMOID_MISMATCH_WITH_BANGO:
        "Do you want to fix the subscription error by synchronizing the promo ID with Bango?",
      DOWNGRADE_NOTIFICATION_FAILED_WITH_STAN:
        "Do you want to fix the subscription error by triggering the notification again?",
    };
    return messages[errorCode] || "Do you want to fix this subscription error?";
  };

  const getRemoveConfirmationMessage = () => {
    return "Do you want to remove this subscription error?";
  };

  const renderConfirmationDialog = (
    action: string,
    selectedError: ISubscriptionError
  ) => {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            padding: "20px",
            borderRadius: "8px",
            textAlign: "center",
            width: "400px",
          }}
        >
          <p>
            {action === "fix"
              ? getFixConfirmationMessage(selectedError.errorCode)
              : getRemoveConfirmationMessage()}
          </p>
          <div style={{ marginTop: "20px" }}>
            <button
              onClick={() =>
                action === "fix"
                  ? fixSubscriptionError(
                      selectedError.subscriptionId,
                      selectedError.contactId,
                      selectedError.errorCode,
                      selectedError.errorDescription
                    )
                  : removeSubscriptionError(
                      selectedError.subscriptionId,
                      selectedError.errorCode
                    )
              }
              style={{
                marginRight: "10px",
                padding: "10px 20px",
                backgroundColor: "green",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Yes
            </button>
            <button
              onClick={() => {
                setSelectedError(null);
                setAction(null);
              }}
              style={{
                padding: "10px 20px",
                backgroundColor: "red",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              No
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: "10px", float: "right" }}>
        <label htmlFor="error-options">Select an option:</label>
        <select
          id="error-options"
          value={errorCodeFilter}
          onChange={(e) => setErrorCodeFilter(e.target.value)}
        >
          <option value="">Select an ErrorCode</option>
          <option value="ACTIVE_SUBSCRIPTION_RENEWAL_DATE_IN_THE_PAST">
            ACTIVE_SUBSCRIPTION_RENEWAL_DATE_IN_THE_PAST
          </option>
          <option value="PROMOID_MISMATCH_WITH_BANGO">
            PROMOID_MISMATCH_WITH_BANGO
          </option>
          <option value="DOWNGRADE_NOTIFICATION_FAILED_WITH_STAN">
            DOWNGRADE_NOTIFICATION_FAILED_WITH_STAN
          </option>
        </select>
      </div>
      <table
        style={{
          width: "100%",
          textAlign: "left",
          borderCollapse: "collapse",
          border: "1px solid black",
        }}
      >
        <thead>
          <tr>
            <th>SubscriptionId</th>
            <th>ContactId</th>
            <th>PlanId</th>
            <th>Status</th>
            <th>RenewalDate</th>
            <th>Error Type</th>
            <th>Error Code</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {subscriptionErrors.map((error) => (
            <tr key={error.subscriptionId}>
              <td>{error.subscriptionId}</td>
              <td>{error.contactId}</td>
              <td>{error.planId}</td>
              <td>{error.status}</td>
              <td>{new Date(error.renewalDate).toLocaleString()}</td>
              <td>{error.errorType}</td>
              <td>{error.errorCode}</td>
              <td>
                <button onClick={() => handleFixClick(error)}>Fix</button>
                <button onClick={() => handleRemoveClick(error)}>Remove</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {subscriptionErrors.length === 0 && (
        <p>No subscription errors to display.</p>
      )}

      {action &&
        selectedError &&
        renderConfirmationDialog(action, selectedError)}
    </div>
  );
};

export default SubscriptionErrorTable;
